import type { AdminCustomerCart } from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { editCartItem, loadCarts, removeCart, removeCartItem } from "../../core/ops/cart-ops";
import { Loader } from "../../core/primitives/loader";
import { shopEntryId } from "../../core/primitives/workspace";
import { CustomerQrController } from "../../sections/customer-qr/customer-qr-controller";
import { type CartItem, CustomerCartsActions, CustomerCartsState } from "./customer-carts-model";
import { TerminalPaymentController } from "./sections/terminal-payment/terminal-payment-controller";

export type CustomerCartsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "providersRepository" | "pendingCartsRepository" | "stripeTerminal"
>;

/**
 * The carts customers built at this shop: what the merchant edits, deletes or
 * takes payment for at the counter. Every write reloads the list rather than
 * patching it, since the server decides what an edit did to the checkout.
 */
export class CustomerCartsController extends SubtreeController implements CustomerCartsActions {
  readonly state: CustomerCartsState;
  readonly terminal: TerminalPaymentController;
  readonly qr: CustomerQrController;
  readonly #list: Loader<string, AdminCustomerCart[]>;
  #shopId: string | null = null;

  constructor(
    readonly deps: CustomerCartsDeps,
    focusCartId: string | null = null,
  ) {
    super();
    this.state = new CustomerCartsState(focusCartId);
    this.subtree.put(CustomerCartsState, this.state);
    this.subtree.put(CustomerCartsActions, this);
    this.#list = this.own(
      new Loader({
        status: this.state.status,
        fetch: (shopId: string) => loadCarts({ ...deps, shopId, locationId: deps.storeRepository.locationId }),
        loaded: (carts) => {
          this.state.carts.value = carts;
        },
      }),
    );
    this.qr = this.own(new CustomerQrController(this.subtree, deps));
    this.sync(() => {
      this.state.openCarts.value = deps.pendingCartsRepository.count;
    }, [deps.pendingCartsRepository]);
    this.terminal = this.own(
      new TerminalPaymentController(this.subtree, deps, {
        onClosed: (paid) => {
          this.state.payingCart.value = null;
          // The webhook marks the cart paid, so the list is asked again rather than told.
          if (paid) this.reload();
        },
      }),
    );
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      this.state.shopId.value = shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      if (shopId === null) {
        this.#list.reset();
        this.state.carts.value = [];
        return;
      }
      void this.#list.load(shopId);
    }, [deps.storeRepository]);
  }

  showQr = () => {
    const { shopId, locationId } = this.deps.storeRepository;
    if (shopId !== null) this.qr.open(shopEntryId(shopId, locationId));
  };

  reload = () => {
    if (this.#shopId !== null) void this.#list.load(this.#shopId);
  };

  toggleInclude = (cart: AdminCustomerCart, item: CartItem) =>
    this.#itemAction(item.id, "Failed to update item.", () =>
      editCartItem({
        ...this.deps,
        customerCartId: cart.id,
        itemId: item.id,
        edit: { included: !item.includedInPurchaseOrder },
      }),
    );

  removeItem = (cart: AdminCustomerCart, item: CartItem) =>
    this.#itemAction(item.id, "Failed to remove item.", () =>
      removeCartItem({ ...this.deps, customerCartId: cart.id, itemId: item.id }),
    );

  promptDelete = (cart: AdminCustomerCart) => {
    this.state.actionError.value = null;
    this.state.deleteError.value = null;
    this.state.confirmDeleteId.value = cart.id;
  };

  cancelDelete = () => {
    this.state.deleteError.value = null;
    this.state.confirmDeleteId.value = null;
  };

  confirmDelete = async (cart: AdminCustomerCart) => {
    this.state.busyCartId.value = cart.id;
    this.state.deleteError.value = null;
    try {
      const r = await removeCart({ ...this.deps, customerCartId: cart.id });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.deleteError.value = { id: cart.id, message: "The request could not be deleted. Please try again." };
        return;
      }
      this.state.confirmDeleteId.value = null;
      this.reload();
    } finally {
      this.state.busyCartId.value = null;
    }
  };

  // Discover and connect as soon as the modal opens: the operator has already
  // said they want to take payment, so a "connect" button first is a step for nothing.
  takePayment = (cart: AdminCustomerCart) => {
    this.state.actionError.value = null;
    this.state.payingCart.value = cart;
    void this.terminal.connect();
  };

  async #itemAction(itemId: string, failMsg: string, op: () => AsyncResult<unknown, Error>): Promise<void> {
    this.state.busyItemId.value = itemId;
    this.state.actionError.value = null;
    try {
      const r = await op();
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.actionError.value = failMsg;
        return;
      }
      this.reload();
    } finally {
      this.state.busyItemId.value = null;
    }
  }
}
